import * as Sentry from '@sentry/react'
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { RouterProvider } from '@tanstack/react-router'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { toast } from 'sonner'
import { setAuthLostHandler } from '@/api/client'
import { apiErrorMessage } from '@/api/errors'
import { createAppRouter } from './router'
import { initSentry } from './sentry'
import './index.css'

initSentry()

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      if (query.meta?.silent) return
      toast.error(apiErrorMessage(error))
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      if (mutation.meta?.silent) return
      // Mutations that set their own onError still toast once here unless marked silent.
      if (mutation.options.onError) return
      toast.error(apiErrorMessage(error))
    },
  }),
  defaultOptions: { queries: { staleTime: 30_000, retry: 1, refetchOnWindowFocus: false } },
})

const router = createAppRouter(queryClient)

setAuthLostHandler(() => {
  queryClient.clear()
  void router.navigate({ to: '/login', search: { redirect: router.state.location.href } })
})

createRoot(document.getElementById('root')!, {
  onUncaughtError: Sentry.reactErrorHandler(),
  onCaughtError: Sentry.reactErrorHandler(),
  onRecoverableError: Sentry.reactErrorHandler(),
}).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>,
)
